import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, AlertCircle, Loader2, MailCheck, ArrowLeft, Send } from 'lucide-react';
import './Login.css';
import './Register.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailVerification() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [state, setState] = useState('loading'); // loading | success | expired | invalid | resent
  const [message, setMessage] = useState('');
  const [resending, setResending] = useState(false);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    if (!token) {
      setState('invalid');
      setMessage('Kein Bestätigungslink gefunden.');
      return;
    }
    (async () => {
      try {
        const resp = await axios.post(`${API_URL}/api/community/verify-email`, { token });
        setState('success');
        setMessage(resp.data.message);
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (detail === 'expired') {
          setState('expired');
          setMessage('Dieser Bestätigungslink ist abgelaufen (gültig für 48 Stunden).');
        } else {
          setState('invalid');
          setMessage('Dieser Bestätigungslink ist ungültig.');
        }
      }
    })();
  }, [token]);

  const handleResend = async () => {
    setResending(true);
    try {
      const resp = await axios.post(`${API_URL}/api/community/resend-verification`, { token });
      setState('resent');
      setMessage(resp.data.message);
    } catch (err) {
      setMessage(err.response?.data?.detail || 'Erneutes Senden fehlgeschlagen. Bitte kontaktiere den Support.');
    }
    setResending(false);
  };

  return (
    <div className="login-page" data-testid="email-verification-page">
      <div className="login-container">
        <div className="login-card success-card">
          {state === 'loading' && (
            <>
              <div className="success-icon-wrap" style={{ color: '#C8A45C' }}>
                <Loader2 size={48} className="spin" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
              <h1>E-Mail wird bestätigt…</h1>
              <p className="success-text">Einen Moment bitte.</p>
            </>
          )}

          {(state === 'success' || state === 'resent') && (
            <>
              <div className="success-icon-wrap">
                {state === 'success' ? <MailCheck size={48} /> : <Send size={48} />}
              </div>
              <h1 data-testid="verify-success-title">{state === 'success' ? 'E-Mail bestätigt!' : 'Neuer Link gesendet!'}</h1>
              <p className="success-text" data-testid="verify-message">{message}</p>
              {state === 'success' && (
                <p className="success-text" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                  Du erhältst eine E-Mail, sobald dein Konto freigeschaltet wurde.
                </p>
              )}
              <Link to="/" className="btn-login" data-testid="verify-back-home">
                <ArrowLeft size={18} />
                <span>Zurück zur Startseite</span>
              </Link>
            </>
          )}

          {(state === 'expired' || state === 'invalid') && (
            <>
              <div className="success-icon-wrap" style={{ color: '#FF6B8A' }}>
                <AlertCircle size={48} />
              </div>
              <h1 data-testid="verify-error-title">{state === 'expired' ? 'Link abgelaufen' : 'Link ungültig'}</h1>
              <p className="success-text" data-testid="verify-message">{message}</p>
              {state === 'expired' && (
                <button className="btn-login" onClick={handleResend} disabled={resending} data-testid="resend-verification-btn">
                  <Send size={18} />
                  <span>{resending ? 'Wird gesendet…' : 'Neuen Bestätigungslink senden'}</span>
                </button>
              )}
              <Link to="/" className="btn-login" style={state === 'expired' ? { marginTop: '0.6rem', opacity: 0.8 } : {}} data-testid="verify-back-home">
                <ArrowLeft size={18} />
                <span>Zurück zur Startseite</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
